// src/components/.../UserDeposit/index.jsx
import React, { useEffect, useMemo, useState } from "react";
import TransactionTable from "./UserDeposit/TransactionTable";
import PaymentMethodService from "../../service/PaymentMethodService";
import DepositService from "../../service/DepositService";
import { useAuth } from "provider/AuthContext";

function UserDeposit() {
  // Payment methods + load state
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  // Form + UI state
  const [transactions, setTransactions] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState("");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [screenshot, setScreenshot] = useState(null);
  const [fileError, setFileError] = useState(""); // added file size error
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [creating, setCreating] = useState(false);

  // Services
  const paymentMethodService = useMemo(() => new PaymentMethodService(), []);
  const depositService = useMemo(() => new DepositService(), []);
  const { user } = useAuth();

  // Load payment methods
  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const methods = await paymentMethodService.fetchPaymentMethods({ activeOnly: true });
      setPaymentMethods(methods || []);
    } catch (err) {
      console.error("Error fetching payment methods:", err);
      setErrorMessage(err?.message || "Failed to fetch payment methods");
    } finally {
      setLoading(false);
    }
  };

  // Load user's deposits
  const fetchDeposits = async () => {
    try {
      const res = await depositService.fetchUserDeposits({ items_per_page: 100 });

      const normalize = (payload) => {
        if (!payload) return [];
        const candidates = [
          payload,
          payload.data,
          payload.items,
          payload.docs,
          payload.results,
          payload.deposits,
          payload.list,
        ];
        for (const c of candidates) {
          if (Array.isArray(c)) return c;
          if (c && Array.isArray(c.items)) return c.items;
        }
        return [];
      };

      const rows = normalize(res);
      setTransactions(rows);
    } catch (err) {
      console.error("Error fetching user deposits:", err);
      setErrorMessage(err?.message || "Failed to fetch your deposits.");
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
    fetchDeposits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasActiveDeposit = transactions?.some((t) => t.status === "pending");

  const selectedMethodDetails = paymentMethods.find((m) => m._id === selectedMethod);
  const isFormValid =
    !!selectedMethod &&
    !!amount &&
    parseFloat(amount) > 0 &&
    !!screenshot &&
    !!user?.username &&
    !hasActiveDeposit &&
    !fileError; // include file size check

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (hasActiveDeposit) {
      setError("You already have an active deposit request.");
      return;
    }
    if (!selectedMethod || !amount || parseFloat(amount) <= 0 || !screenshot) {
      setError("Please select a method, enter a valid amount, and upload a payment proof.");
      return;
    }
    if (!user?.username) {
      setError("You must be logged in to submit a deposit.");
      return;
    }
    if (fileError) {
      setError(fileError);
      return;
    }

    try {
      setCreating(true);
      const depositData = {
        paymentMethod: selectedMethod,
        amount: parseFloat(amount),
        username: user.username,
        notes,
      };

      await depositService.createDeposit(depositData, screenshot);

      setSuccess("Deposit request submitted successfully!");
      setSelectedMethod("");
      setAmount("");
      setNotes("");
      setScreenshot(null);
      setFileError("");

      await fetchDeposits();
    } catch (err) {
      console.error("Error creating deposit:", err);
      setError(err?.message || "Failed to submit deposit request.");
    } finally {
      setCreating(false);
    }
  };

  const renderAccountDetails = (method) => {
    if (!method.accountDetails || Object.keys(method.accountDetails).length === 0) {
      return (
        <div className="mt-2 bg-gray-800 p-3 rounded-md text-sm">
          <p className="text-gray-400 text-center">No account details required for this method.</p>
        </div>
      );
    }

    const formatKey = (key) => {
      return key
        .replace(/([A-Z])/g, " $1")
        .replace(/([A-Z][a-z])/g, " $1")
        .replace(/_/g, " ")
        .replace(/(^\w|\s\w)/g, (letter) => letter.toUpperCase())
        .replace(/\b\w/g, (l) => l.toUpperCase())
        .trim();
    };

    const formatValue = (key, value) => {
      if (Array.isArray(value)) return value.join(", ");
      if (typeof value === "boolean") return value ? "Yes" : "No";

      const sensitiveKeys = ["address", "wallet", "account", "number", "key", "token", "secret"];
      const isSensitive = sensitiveKeys.some((s) => key.toLowerCase().includes(s));

      if (isSensitive) {
        if (key.toLowerCase().includes("address") || key.toLowerCase().includes("wallet")) {
          return <span className="text-green-400 font-mono break-all text-xs">{value}</span>;
        }
        if (typeof value === "string" && value.length > 8 && /^\d+$/.test(value)) {
          return `••••${value.slice(-4)}`;
        }
      }
      return value;
    };

    const isImportantField = (key) => {
      const importantKeys = [
        "walletAddress",
        "address",
        "accountNumber",
        "accountName",
        "publicKey",
        "minAmount",
        "maxAmount",
      ];
      return importantKeys.some((important) => key.toLowerCase().includes(important.toLowerCase()));
    };

    return (
      <div className="mt-2 bg-gray-800 rounded-md overflow-hidden">
        <div className="grid gap-0">
          {Object.entries(method.accountDetails).map(([key, value], index, arr) => (
            <div
              key={key}
              className={`flex justify-between items-start py-3 px-4 ${
                index !== arr.length - 1 ? "border-b border-gray-700" : ""
              } ${isImportantField(key) ? "bg-gray-700/30" : ""}`}
            >
              <span className="font-medium text-gray-300 text-sm flex-shrink-0 mr-4">{formatKey(key)}</span>
              <span className="text-right text-sm break-words max-w-[60%]">{formatValue(key, value)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const depositMethods = paymentMethods.filter(
    (method) => method.isActive && (method.type === "deposit" || method.type === "both")
  );

  if (loading) {
    return (
      <div className="text-white p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p>Loading payment methods...</p>
      </div>
    );
  }

  return (
    <div className="text-white md:px-5 md:py-4 p-2 bg-[#000c24] md:bg-transparent max-w-screen-md mx-auto overflow-auto">
      <h1 className="md:text-[26px] text-[20px] font-semibold md:mb-6 mb-4 text-center md:text-left uppercase">
        Make a Deposit
      </h1>

      {errorMessage && (
        <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-6">{errorMessage}</div>
      )}
      {error && (
        <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-6">{error}</div>
      )}
      {success && (
        <div className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded-lg mb-6">{success}</div>
      )}
      {hasActiveDeposit && (
        <div className="bg-yellow-500/20 border border-yellow-500 text-yellow-400 px-4 py-3 rounded-lg mb-6">
          You have an active deposit request. Please wait for it to be processed.
        </div>
      )}
      {!user?.username && (
        <div className="bg-blue-500/20 border border-blue-500 text-blue-300 px-4 py-3 rounded-lg mb-6">
          Please log in to submit a deposit.
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 mb-8">
        {/* Payment method */}
        <div className="flex flex-col w-full">
          <label className="text-gray-400 font-medium mb-2">
            Payment Method<span className="text-red-500 ml-1">*</span>
          </label>
          <select
            value={selectedMethod}
            onChange={(e) => setSelectedMethod(e.target.value)}
            required
            disabled={hasActiveDeposit || loading}
            className="border border-gray-600 bg-[#000c24] text-white rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-50"
          >
            <option value="">Select a payment method</option>
            {depositMethods
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((method) => (
                <option key={method._id} value={method._id}>
                  {method.logo} {method.name}
                </option>
              ))}
          </select>
        </div>

        {/* Method details */}
        {selectedMethodDetails && (
          <div className="border border-gray-600 rounded-lg p-4 bg-gray-900">
            <div className="flex items-center space-x-3 mb-4">
              <span className="text-2xl">{selectedMethodDetails.logo}</span>
              <div className="flex-1">
                <h3 className="font-semibold text-white text-lg">{selectedMethodDetails.name}</h3>
                <p className="text-gray-400 text-sm mt-1">{selectedMethodDetails.description}</p>
              </div>
            </div>

            {selectedMethodDetails.accountDetails &&
              Object.keys(selectedMethodDetails.accountDetails).length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-300 text-sm uppercase tracking-wide mb-2">
                    Payment Details
                  </h4>
                  {renderAccountDetails(selectedMethodDetails)}
                </div>
              )}

            {selectedMethodDetails.instructions && (
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-md">
                <p className="text-blue-400 text-sm font-medium">Instructions:</p>
                <p className="text-blue-300 text-sm mt-1">{selectedMethodDetails.instructions}</p>
              </div>
            )}
          </div>
        )}

        {/* Amount */}
        {selectedMethod && (
          <div className="flex flex-col w-full">
            <label className="text-gray-400 font-medium mb-2">
              Montant<span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={"Montant du dépôt"}
              min="1"
              step="0.01"
              required
              disabled={hasActiveDeposit}
              className="border border-gray-600 bg-[#000c24] text-white rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
        )}

        {/* Notes
        {selectedMethod && (
          <div className="flex flex-col w-full">
            <label className="text-gray-400 font-medium mb-2">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any reference or remarks for the admin"
              rows={3}
              disabled={hasActiveDeposit}
              className="border border-gray-600 bg-[#000c24] text-white rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
        )} */}

        {/* Screenshot */}
        {selectedMethod && amount && (
          <div className="flex flex-col w-full">
            <label className="text-gray-400 font-medium mb-2">
              Upload Payment Proof<span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  const MAX_SIZE = 4 * 1024 * 1024;
                  if (file.size > MAX_SIZE) {
                    setFileError(`File size must not exceed 4 MB.`);
                    setScreenshot(null);
                  } else {
                    setScreenshot(file);
                    setFileError("");
                  }
                } else {
                  setFileError("");
                  setScreenshot(null);
                }
              }}
              required
              disabled={hasActiveDeposit}
              className="border border-gray-600 bg-[#000c24] text-white rounded-lg py-2 px-3 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-gray-900 hover:file:bg-primary/70"
            />
            {fileError && <p className="text-red-400 text-sm mt-2">{fileError}</p>}
            {screenshot && !fileError && (
              <p className="text-green-400 text-sm mt-2">{screenshot.name} selected</p>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={!isFormValid || creating}
          className="bg-primary/80 text-gray-900 py-3 px-6 rounded-lg hover:bg-primary/70 transition w-full font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {creating ? "Submitting..." : hasActiveDeposit ? "ACTIVE REQUEST PENDING" : "SUBMIT DEPOSIT"}
        </button>
      </form>

      <TransactionTable title="Deposit History" transactions={transactions} paymentMethods={paymentMethods} />
    </div>
  );
}

export default UserDeposit;
